/**
 * fs/local.js — real-filesystem backend for Numkit IDE.
 *
 * Two interchangeable backends with the same public shape:
 *
 *  NATIVE  — Electron desktop. Uses window.nativeFS exposed by the
 *            shell's preload.js (see ide/desktop/preload.js). Paths
 *            are OS absolute paths; every operation round-trips
 *            through ipcMain, which enforces a sandbox inside the
 *            chosen mount root.
 *
 *  FSA     — Web (Chromium-family browsers). Uses window.showDirectoryPicker
 *            and FileSystemDirectoryHandle. Sandboxed by the browser;
 *            no OS paths, no "reveal in explorer" possible.
 *
 * Selection happens once at module load: if window.nativeFS is present
 * we're in the Electron shell and use it; else if showDirectoryPicker
 * exists we fall back to FSA; else the backend is unavailable and the
 * Local Folder source is hidden from the UI.
 *
 * Public shape (mirrors temporary.js where it overlaps):
 *   isAvailable, supportsReveal,
 *   isMounted, mountName,
 *   pickDirectory, reconnect, disconnect,
 *   listTree, readFile, writeFile, mkdir, exists, remove, rename, clear,
 *   revealInExplorer, showItemInFolder.
 */

// ─────────────────────────────────────────────────────────────────
// Native backend — Electron desktop
// ─────────────────────────────────────────────────────────────────
//
// Persists the root folder path in localStorage ("numkit.ide.native.root")
// so reloading the IDE reopens the same folder without asking again.
// No permission handshake is needed — the main process runs with
// the user's OS permissions.

const NATIVE_ROOT_KEY = 'numkit.ide.native.root';

function makeNativeBackend() {
    const api = window.nativeFS;
    let rootPath = null;
    let rootDisplayName = null;

    function stored() {
        try { return localStorage.getItem(NATIVE_ROOT_KEY); }
        catch (_) { return null; }
    }
    function remember(p) {
        try { if (p) localStorage.setItem(NATIVE_ROOT_KEY, p); else localStorage.removeItem(NATIVE_ROOT_KEY); }
        catch (_) {}
    }
    function setRoot(p) {
        rootPath = p;
        if (p) {
            // Last segment of the path — works for Win and POSIX.
            const parts = p.split(/[\\/]/).filter(Boolean);
            rootDisplayName = parts.length ? parts[parts.length - 1] : p;
        } else {
            rootDisplayName = null;
        }
    }

    const initSaved = stored();
    if (initSaved) {
        setRoot(initSaved);
    }

    return {
        kind: 'native',
        isAvailable: () => true,
        supportsReveal: () => true,

        root: () => rootPath,
        isMounted: () => rootPath !== null,
        mountName: () => rootDisplayName,

        setRootPath(p) {
            if (!p) return null;
            setRoot(p);
            remember(p);
            return rootDisplayName;
        },

        async pickDirectory() {
            const picked = await api.pickDirectory();
            if (!picked) return null;
            setRoot(picked);
            remember(picked);
            return rootDisplayName;
        },

        async reconnect() {
            let saved = stored();
            if (!saved && typeof api.getUserHome === 'function') {
                try { saved = await api.getUserHome(); } catch (_) {}
            }
            if (!saved) return null;
            // Verify the path is still reachable — avoids surfacing a
            // stale mount after the folder was renamed/removed from disk.
            try {
                if (typeof api.listDir === 'function') {
                    await api.listDir(saved, '/');
                } else {
                    await api.listTree(saved);
                }
                setRoot(saved);
                remember(saved);
                return rootDisplayName;
            } catch (_) {
                if (typeof api.getUserHome === 'function') {
                    try {
                        const home = await api.getUserHome();
                        if (home) {
                            if (typeof api.listDir === 'function') {
                                await api.listDir(home, '/');
                            } else {
                                await api.listTree(home);
                            }
                            setRoot(home);
                            remember(home);
                            return rootDisplayName;
                        }
                    } catch (_) {}
                }
                remember(null);
                return null;
            }
        },

        async disconnect() {
            let home = '';
            if (typeof api.getUserHome === 'function') {
                try { home = await api.getUserHome(); } catch (_) {}
            }
            if (home) {
                setRoot(home);
                remember(home);
            } else {
                setRoot(null);
                remember(null);
            }
        },

        async listDir(relPath = '/') {
            if (!rootPath) return [];
            return api.listDir(rootPath, relPath);
        },
        async listTree() {
            if (!rootPath) return [];
            return api.listTree(rootPath);
        },
        async readFile(p)                { return rootPath ? api.readFile(rootPath, p) : null; },
        // Synchronous read used by the WASM engine's csvread/load
        // callbacks. Available only on the native (Electron) backend
        // because FSA on the web is async-only. Returns the file
        // content directly or throws — caller handles "missing".
        readFileSync(p) {
            if (!rootPath || typeof api.readFileSync !== 'function') {
                throw new Error('local: not mounted or sync read unavailable');
            }
            const res = api.readFileSync(rootPath, p);
            if (res?.error) throw new Error(res.error);
            return res?.content ?? null;
        },
        // Binary sync read — raw bytes (Uint8Array) for imread/audioread.
        // Returns null for a missing file so the adapter can throw a clean
        // "no such file"; throws on a real backend error.
        readFileBytesSync(p) {
            if (!rootPath || typeof api.readFileBytesSync !== 'function') {
                throw new Error('local: not mounted or binary sync read unavailable');
            }
            const res = api.readFileBytesSync(rootPath, p);
            if (res?.error) throw new Error(res.error);
            return res?.bytes ?? null;
        },
        existsSync(p) {
            if (!rootPath || typeof api.existsSync !== 'function') return false;
            return api.existsSync(rootPath, p);
        },
        async writeFile(p, content)      { return api.writeFile(rootPath, p, content); },
        // Binary write — raw bytes (imwrite/audiowrite).
        async writeFileBytes(p, bytes)   { return api.writeFileBytes(rootPath, p, bytes); },
        async mkdir(p)                   { return api.mkdir(rootPath, p); },
        async exists(p)                  { return rootPath ? api.exists(rootPath, p) : false; },
        async remove(p)                  { return api.remove(rootPath, p); },
        async rename(oldP, newP)         { return api.rename(rootPath, oldP, newP); },

        async revealInExplorer(p)        {
            if (!rootPath) return;
            return api.revealInExplorer(rootPath, p || '');
        },
        async showItemInFolder(p)        {
            if (!rootPath) return;
            return api.showItemInFolder(rootPath, p);
        },

        async clear() { /* never wipe a real disk */ },
    };
}

// ─────────────────────────────────────────────────────────────────
// FSA backend — Chromium web
// ─────────────────────────────────────────────────────────────────
//
// Stores the FileSystemDirectoryHandle in IndexedDB so reloading
// preserves the mount across page reloads (still needs a permission
// re-grant from the user).

const DB_NAME = 'numkit-ide-local-fs';
const DB_VERSION = 1;
const STORE = 'handles';
const HANDLE_KEY = 'root';

function makeFsaBackend() {
    let db = null;
    let rootHandle = null;

    function openDB() {
        return new Promise((resolve, reject) => {
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = e => {
                const d = e.target.result;
                if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE);
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }
    async function ensureDB() {
        if (!db) db = await openDB();
        return db;
    }
    function idbGet(key) {
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const req = tx.objectStore(STORE).get(key);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }
    function idbPut(key, val) {
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).put(val, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    function idbDelete(key) {
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    function splitPath(p) { return p.replace(/^\/+/, '').split('/').filter(Boolean); }
    function joinPath(parent, name) { return (!parent || parent === '/') ? '/' + name : parent + '/' + name; }

    async function resolveDir(p, { create = false } = {}) {
        if (!rootHandle) throw new Error('No folder mounted');
        let cur = rootHandle;
        for (const part of splitPath(p)) cur = await cur.getDirectoryHandle(part, { create });
        return cur;
    }
    async function resolveParent(p, { create = false } = {}) {
        const parts = splitPath(p);
        if (parts.length === 0) throw new Error('Cannot resolve root as file');
        const name = parts.pop();
        let cur = rootHandle;
        for (const part of parts) cur = await cur.getDirectoryHandle(part, { create });
        return { parent: cur, name };
    }

    async function verifyPermission(handle, mode = 'readwrite') {
        if (!handle.queryPermission) return true;
        const opts = { mode };
        if ((await handle.queryPermission(opts)) === 'granted') return true;
        if ((await handle.requestPermission(opts)) === 'granted') return true;
        return false;
    }

    return {
        kind: 'fsa',
        isAvailable: () => typeof window.showDirectoryPicker === 'function',
        supportsReveal: () => false,

        isMounted: () => rootHandle !== null,
        mountName: () => (rootHandle ? rootHandle.name : null),

        async pickDirectory() {
            let handle;
            try { handle = await window.showDirectoryPicker({ mode: 'readwrite' }); }
            catch (err) { if (err?.name === 'AbortError') return null; throw err; }
            if (!await verifyPermission(handle, 'readwrite'))
                throw new Error('Read/write permission denied for folder');
            rootHandle = handle;
            await ensureDB();
            await idbPut(HANDLE_KEY, handle);
            return handle.name;
        },

        async reconnect() {
            await ensureDB();
            const stored = await idbGet(HANDLE_KEY);
            if (!stored) return null;
            if (!await verifyPermission(stored, 'readwrite')) return null;
            rootHandle = stored;
            return stored.name;
        },

        async disconnect() {
            rootHandle = null;
            await ensureDB();
            await idbDelete(HANDLE_KEY);
        },

        async listDir(relPath = '/') {
            if (!rootHandle) return [];
            try {
                const targetDir = await resolveDir(relPath);
                const entries = [];
                for await (const [name, handle] of targetDir.entries()) {
                    const cleanRel = (relPath || '/').replace(/\\/g, '/').replace(/\/+$/, '');
                    const itemPath = cleanRel === '' || cleanRel === '/' ? `/${name}` : `${cleanRel}/${name}`;
                    let size = undefined;
                    let modified = undefined;
                    if (handle.kind === 'file') {
                        try {
                            const fileObj = await handle.getFile();
                            size = fileObj.size;
                            modified = fileObj.lastModified;
                        } catch (_) {}
                    }
                    entries.push({
                        name,
                        path: itemPath,
                        type: handle.kind === 'directory' ? 'folder' : 'file',
                        size,
                        modified,
                    });
                }
                entries.sort((a, b) => (a.type !== b.type) ? (a.type === 'folder' ? -1 : 1) : a.name.localeCompare(b.name));
                return entries;
            } catch (_) { return []; }
        },

        async listTree() {
            if (!rootHandle) return [];
            const root = [];
            async function walk(dirHandle, pathStr, bucket) {
                const entries = [];
                for await (const [name, handle] of dirHandle.entries()) {
                    const itemPath = joinPath(pathStr, name);
                    const node = { name, path: itemPath, type: handle.kind === 'directory' ? 'folder' : 'file' };
                    if (handle.kind === 'directory') {
                        node.children = [];
                        await walk(handle, itemPath, node.children);
                    }
                    entries.push(node);
                }
                entries.sort((a, b) => (a.type !== b.type) ? (a.type === 'folder' ? -1 : 1) : a.name.localeCompare(b.name));
                bucket.push(...entries);
            }
            await walk(rootHandle, '', root);
            return root;
        },

        async readFile(p) {
            if (!rootHandle) return null;
            try {
                const { parent, name } = await resolveParent(p);
                const fh = await parent.getFileHandle(name);
                return await (await fh.getFile()).text();
            } catch (err) {
                if (err && (err.name === 'NotFoundError' || err.name === 'TypeMismatchError')) return null;
                throw err;
            }
        },

        async writeFile(p, content) {
            const { parent, name } = await resolveParent(p, { create: true });
            const fh = await parent.getFileHandle(name, { create: true });
            const w = await fh.createWritable();
            await w.write(content ?? '');
            await w.close();
        },

        async mkdir(p) { if (!p || p === '/') return; await resolveDir(p, { create: true }); },

        async exists(p) {
            if (!rootHandle) return false;
            try {
                const { parent, name } = await resolveParent(p);
                try { await parent.getFileHandle(name); return true; } catch (_) {}
                try { await parent.getDirectoryHandle(name); return true; } catch (_) {}
                return false;
            } catch (_) { return false; }
        },

        async remove(p) {
            const { parent, name } = await resolveParent(p);
            await parent.removeEntry(name, { recursive: true });
        },

        async rename(oldPath, newPath) {
            const oldParts = splitPath(oldPath);
            const newParts = splitPath(newPath);
            if (oldParts.length !== newParts.length) throw new Error('Cross-folder rename is not supported yet');
            for (let i = 0; i < oldParts.length - 1; i++) {
                if (oldParts[i] !== newParts[i]) throw new Error('Cross-folder rename is not supported yet');
            }
            const { parent, name: oldName } = await resolveParent(oldPath);
            const newName = newParts[newParts.length - 1];
            if (oldName === newName) return;
            const srcFile = await parent.getFileHandle(oldName).catch(() => null);
            if (srcFile) {
                const content = await (await srcFile.getFile()).text();
                const dst = await parent.getFileHandle(newName, { create: true });
                const w = await dst.createWritable();
                await w.write(content);
                await w.close();
                await parent.removeEntry(oldName);
                return;
            }
            const srcDir = await parent.getDirectoryHandle(oldName).catch(() => null);
            if (srcDir) {
                async function copyDir(from, toParent, toName) {
                    const to = await toParent.getDirectoryHandle(toName, { create: true });
                    for await (const [nm, h] of from.entries()) {
                        if (h.kind === 'file') {
                            const content = await (await h.getFile()).text();
                            const fh = await to.getFileHandle(nm, { create: true });
                            const w = await fh.createWritable();
                            await w.write(content);
                            await w.close();
                        } else {
                            await copyDir(h, to, nm);
                        }
                    }
                }
                await copyDir(srcDir, parent, newName);
                await parent.removeEntry(oldName, { recursive: true });
                return;
            }
            throw new Error('Rename source not found');
        },

        // Reveal is not possible in the FSA world — handles hide OS paths
        // and browsers can't launch the file manager. These are no-ops so
        // callers don't have to branch; the UI also gates on supportsReveal.
        async revealInExplorer(_p) { /* unsupported in FSA */ },
        async showItemInFolder(_p) { /* unsupported in FSA */ },

        async clear() { /* never wipe a real disk */ },
    };
}

// ─────────────────────────────────────────────────────────────────
// Dispatcher
// ─────────────────────────────────────────────────────────────────

function pickBackend() {
    if (typeof window === 'undefined') return null;
    if (window.nativeFS) return makeNativeBackend();
    return null;
}

const backend = pickBackend();

const unavailable = {
    kind: 'none',
    isAvailable: () => false,
    supportsReveal: () => false,
    isMounted: () => false,
    mountName: () => null,
    async pickDirectory() { throw new Error('Local Folder not available in this browser'); },
    async reconnect() { return null; },
    async disconnect() {},
    async listDir() { return []; },
    async listTree() { return []; },
    async readFile() { return null; },
    async writeFile() {},
    async mkdir() {},
    async exists() { return false; },
    async remove() {},
    async rename() {},
    async revealInExplorer() {},
    async showItemInFolder() {},
    async clear() {},
};

const localFS = backend || unavailable;
export default localFS;
